<script setup lang="ts">
  /**
   * Search field — 03-components/15-form-inputs (icon affix) · OSCloud DS 1.1.151
   *
   * The form field with its leading glyph affix: one magnifier in the control's leading slot,
   * cleared by `__input--with-icon-affix`. The one text input allowed to drop its visible label
   * (the DS's search exemption) — in exchange `type="search"` and an accessible name are
   * required, so `label` is required and becomes the input's `aria-label` unless `showLabel`
   * puts it on screen. The placeholder says what can be searched; it is never the name.
   *
   * It submits nothing: a register filters as the value changes.
   */
  import BfIconSearch from '@/components/ui/icons/BfIconSearch.vue'

  withDefaults(
    defineProps<{
      modelValue?: string
      /** The field's accessible name — the label's words, not the placeholder's. */
      label: string
      /** Renders `label` as the visible uppercase micro-label instead of an aria-label. */
      showLabel?: boolean
      placeholder?: string
      disabled?: boolean
      fullWidth?: boolean
      /** 'md' (default 44px, 16px glyph) or 'lg' (52px, 18px glyph). */
      size?: 'md' | 'lg'
      autocomplete?: string
      id?: string
    }>(),
    {
      modelValue: '',
      showLabel: false,
      placeholder: '',
      disabled: false,
      fullWidth: false,
      size: 'md',
      autocomplete: 'off',
      id: undefined,
    },
  )

  defineEmits<{ 'update:modelValue': [value: string] }>()

  // Fall-through attrs (aria-describedby, aria-controls, …) belong to the input itself.
  defineOptions({ inheritAttrs: false })
</script>

<template>
  <div class="form-field" :class="{ 'form-field--large': size === 'lg', 'form-field--full-width': fullWidth }">
    <label v-if="showLabel" :for="id" class="form-field__label">{{ label }}</label>
    <div class="form-field__control">
      <span class="form-field__affix form-field__affix--icon" aria-hidden="true">
        <BfIconSearch :size="size === 'lg' ? 18 : 16" />
      </span>
      <input
        :id="id"
        v-bind="$attrs"
        class="form-field__input form-field__input--with-icon-affix"
        type="search"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :aria-label="showLabel ? undefined : label"
        @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  /* The form-control foundation, restated from the --bf-form-* tokens because BfInput's rules are
     scoped to BfInput. Only the icon affix and its clearance are this control's own. */
  .form-field {
    display: flex;
    flex-direction: column;
    min-width: 0;

    &--full-width {
      width: 100%;
    }

    &__label {
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      color: var(--bf-off-black);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-eyebrow);
    }

    > .form-field__label {
      margin-bottom: var(--bf-form-label-gap);
    }

    &__control {
      position: relative;
    }

    &__input {
      width: 100%;
      height: var(--bf-form-control-height);
      padding: 0 14px;
      font-family: var(--bf-font-body);
      font-size: 15px;
      color: var(--bf-fg-1);
      background: var(--bf-form-control-surface);
      border: 1px solid var(--bf-form-control-hairline);
      border-radius: var(--bf-form-control-radius);
      transition:
        border-color var(--bf-dur-fast) var(--bf-ease),
        box-shadow var(--bf-dur-fast) var(--bf-ease),
        background var(--bf-dur-fast) var(--bf-ease);

      &::placeholder {
        color: var(--bf-form-control-placeholder);
      }

      &:hover:not(:focus):not(:disabled) {
        border-color: var(--bf-form-control-hairline-hover);
      }

      &:focus-visible {
        outline: none;
        border-color: var(--bf-form-control-hairline-focus);
        box-shadow: var(--bf-form-focus-ring);
        background: var(--bf-form-control-surface-focus);
      }

      &:disabled {
        opacity: var(--bf-form-disabled-opacity);
        cursor: not-allowed;
      }

      /* 42px = 14px inset + 16px glyph + 12px gap to the value. */
      &--with-icon-affix {
        padding-left: 42px;
      }
    }

    /* Decoration only: inert, so a click lands in the input, and aria-hidden, so the field is
       named by its label. font-size: 0 leaves the flex centring to the glyph alone. */
    &__affix {
      position: absolute;
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
    }

    &__affix--icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 0;
      color: var(--bf-fg-3);
    }

    /* Steps with the wrapper size: 52px control, 18px glyph, 46px clearance. */
    &--large &__input {
      height: var(--bf-form-control-height-large);
      font-size: var(--bf-fs-base);
    }

    &--large &__input--with-icon-affix {
      padding-left: 46px;
    }
  }
</style>
